import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import financeService from '../../../../services/financeService';
import { Reconciliation } from '../../../../types/finance';
import { useModulePermissions } from '../../../../hooks/useModulePermissions';
import JournalEntryDrawer from '../../journal/JournalEntryDrawer';
import ReconciliationWorkspace from './ReconciliationWorkspace';
import ReconciliationReport from './ReconciliationReport';

/** One reconciliation: the workspace while open, the report once complete. */
const ReconciliationView: React.FC<{ id: string; onBack: () => void }> = ({ id, onBack }) => {
  const { canWrite } = useModulePermissions();
  const [rec, setRec] = useState<Reconciliation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRec(await financeService.getReconciliation(id));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the reconciliation.');
    }
  }, [id]);

  useEffect(() => { setRec(null); load(); }, [load]);

  return (
    <Box>
      <Button size="small" startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 1.5, ml: -0.5 }}>All bank accounts</Button>
      {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
      {!rec && !error && (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" height={88} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: 2 }} />
        </Stack>
      )}
      {rec && (rec.status === 'OPEN'
        ? <ReconciliationWorkspace rec={rec} editable={canWrite('bank_reconciliation')} onLocal={setRec} onReload={load} onOpenEntry={setOpenEntryId} onDone={onBack} />
        : <ReconciliationReport rec={rec} onOpenEntry={setOpenEntryId} />)}
      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={load} />
    </Box>
  );
};

export default ReconciliationView;
